/* ==========================================================================
   pdp.js - Clean-room Product page behavior, matched to the mockup.
   Vanilla JS, no jQuery, no theme.js. IIFE. Scoped to each [data-pdp].
   Progressive enhancement: the real <form> POST still works without JS.
   ASCII-only.
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); } else { document.addEventListener('DOMContentLoaded', fn); }
  }
  function noop() {}
  function isHeroCarousel() { return !!(window.matchMedia && window.matchMedia('(max-width: 720px)').matches); }
  var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  ready(function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp]'), initPdp);
  });

  function initPdp(root) {
    if (root.__pdpInit) { return; }
    root.__pdpInit = true;
    var gallery = initGallery(root);
    initHeroCarousel(root, gallery);
    initRail(root, gallery);
    initZoom(root, gallery);
    initDescription(root);
    initDeliveryCountdown(root);
    initQty(root);
    initVariants(root, gallery);
    initCart(root);
    initUpsell(root);
  }

  /* === GALLERY: thumb -> hero slide swap + active state === */
  function initGallery(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-thumb]'));
    if (!slides.length) { return { select: noop, selectByMediaId: noop, current: function () { return 0; }, images: [] }; }

    var images = slides.map(function (s) {
      var img = s.querySelector('img');
      return {
        index: Number(s.getAttribute('data-index')),
        mediaId: s.getAttribute('data-media-id'),
        src: img ? (img.getAttribute('data-zoom-src') || img.currentSrc || img.src) : '',
        thumb: '',
        alt: img ? img.alt : ''
      };
    });
    thumbs.forEach(function (t) {
      var i = Number(t.getAttribute('data-index'));
      var im = t.querySelector('img');
      if (images[i] && im) { images[i].thumb = im.src; }
    });

    var heroWrap = root.querySelector('[data-pdp-hero-wrap]');
    var current = 0;
    var subs = [];
    slides.forEach(function (s, i) { if (s.classList.contains('is-active')) { current = i; } });

    function setupImageLoader(slide) {
      var img = slide.querySelector('img');
      if (!img) { return; }
      img.decoding = 'async';
      if (!slide.querySelector('.pdp-image-loader')) {
        var loader = document.createElement('span');
        loader.className = 'pdp-image-loader';
        loader.setAttribute('aria-hidden', 'true');
        slide.appendChild(loader);
      }
      function done() {
        slide.classList.remove('is-loading');
        slide.classList.add('is-loaded');
      }
      if (img.complete && img.naturalWidth > 0) {
        done();
      } else {
        slide.classList.add('is-loading');
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    }

    function primeSlideImage(slide) {
      var img = slide && slide.querySelector('img');
      if (!img) { return; }
      img.decoding = 'async';
      if (img.loading === 'lazy') { img.loading = 'eager'; }
      setupImageLoader(slide);
      if (img.decode && !(img.complete && img.naturalWidth > 0)) {
        img.decode().catch(noop).then(function () {
          if (img.complete) {
            slide.classList.remove('is-loading');
            slide.classList.add('is-loaded');
          }
        });
      }
    }

    function primeAround(i) {
      if (i == null || i < 0 || i >= slides.length) { return; }
      primeSlideImage(slides[i]);
      if (i > 0) { primeSlideImage(slides[i - 1]); }
      if (i < slides.length - 1) { primeSlideImage(slides[i + 1]); }
    }

    slides.forEach(setupImageLoader);
    primeAround(current);

    function setHeroOffset() {
      if (heroWrap) { heroWrap.style.setProperty('--hero-offset', (current * -100) + '%'); }
    }
    function select(i, opts) {
      if (i == null || i < 0 || i >= slides.length) { return; }
      current = i;
      primeAround(i);
      var jump = isHeroCarousel() && opts && opts.behavior === 'auto';
      if (jump && heroWrap) { heroWrap.classList.add('is-jumping'); }
      slides.forEach(function (s, k) {
        var on = k === i;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', isHeroCarousel() ? 'false' : (on ? 'false' : 'true'));
      });
      thumbs.forEach(function (t) {
        var on = Number(t.getAttribute('data-index')) === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
        if (on && !isHeroCarousel()) { t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: rm ? 'auto' : 'smooth' }); }
      });
      setHeroOffset();
      subs.forEach(function (fn) { fn(current); });
      if (jump && heroWrap) { requestAnimationFrame(function () { heroWrap.classList.remove('is-jumping'); }); }
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function (e) { e.preventDefault(); select(Number(t.getAttribute('data-index'))); });
    });

    setHeroOffset();
    window.addEventListener('resize', setHeroOffset);

    return {
      select: select,
      selectByMediaId: function (id) {
        if (id == null) { return; }
        for (var k = 0; k < slides.length; k++) {
          if (slides[k].getAttribute('data-media-id') === String(id)) { select(k); return; }
        }
        select(0); // variant featured media not in the gallery -> show the first slide
      },
      current: function () { return current; },
      images: images,
      slides: slides,
      count: slides.length,
      primeAround: primeAround,
      onChange: function (fn) { if (typeof fn === 'function') { subs.push(fn); } },
      step: function (dir) {
        if (isHeroCarousel()) {
          var ni = Math.max(0, Math.min(slides.length - 1, current + dir));
          if (ni !== current) { select(ni); }
        } else {
          select((current + dir + slides.length) % slides.length);
        }
      }
    };
  }

  /* === MOBILE HERO SWIPE CAROUSEL + DOTS/ARROWS (<=720px) ===
     Hooks: [data-pdp-dots], [data-pdp-dot-list], [data-pdp-dot-prev/next], generated
     [data-pdp-dot-index]; shares root.__pdpHeroDrag with initZoom so a swipe does not
     open the zoom modal (tap still does). Desktop is inert (matchMedia gates it). */
  function initHeroCarousel(root, gallery) {
    var heroWrap = root.querySelector('[data-pdp-hero-wrap]');
    var dots = root.querySelector('[data-pdp-dots]');
    var dotList = root.querySelector('[data-pdp-dot-list]');
    var prev = root.querySelector('[data-pdp-dot-prev]');
    var next = root.querySelector('[data-pdp-dot-next]');
    var count = gallery.count || 0;
    if (count <= 1) { if (dots) { dots.hidden = true; } return; }

    var touchStart = null;
    var touchLock = null;
    var st = { x: null, y: null, delta: 0, dragged: false, suppressClick: false, lock: null, pointerId: null, startedAt: 0, timer: null };
    root.__pdpHeroDrag = st;

    if (dotList) {
      dotList.innerHTML = '';
      for (var n = 0; n < count; n++) {
        (function (idx) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'gallery-dot';
          b.setAttribute('data-pdp-dot-index', idx);
          b.setAttribute('aria-label', 'View product image ' + (idx + 1));
          b.addEventListener('click', function () { gallery.select(idx, { behavior: 'auto' }); });
          dotList.appendChild(b);
        })(n);
      }
    }

    function syncNav(i) {
      var mobile = isHeroCarousel();
      if (prev) { prev.hidden = false; prev.disabled = mobile && i === 0; prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false'); }
      if (next) { next.hidden = false; next.disabled = mobile && i === count - 1; next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false'); }
      if (dotList) {
        Array.prototype.forEach.call(dotList.children, function (b) {
          var on = Number(b.getAttribute('data-pdp-dot-index')) === i;
          b.classList.toggle('is-active', on);
          if (on) { b.setAttribute('aria-current', 'true'); } else { b.removeAttribute('aria-current'); }
        });
      }
    }
    gallery.onChange(syncNav);
    syncNav(gallery.current());
    window.addEventListener('resize', function () { syncNav(gallery.current()); });

    if (prev) { prev.addEventListener('click', function () { gallery.step(-1); }); }
    if (next) { next.addEventListener('click', function () { gallery.step(1); }); }

    function holdClickSuppression() {
      if (st.timer) { clearTimeout(st.timer); }
      st.timer = setTimeout(function () {
        st.dragged = false;
        st.suppressClick = false;
        st.timer = null;
      }, 320);
    }
    function down(e) {
      if (!isHeroCarousel() || count < 2) { return; }
      if (e.pointerType === 'mouse' && e.button !== 0) { return; }
      if (st.timer) { clearTimeout(st.timer); st.timer = null; }
      st.x = e.clientX; st.y = e.clientY; st.delta = 0; st.dragged = false;
      st.suppressClick = false; st.lock = null; st.pointerId = e.pointerId; st.startedAt = Date.now();
      if (gallery.primeAround) { gallery.primeAround(gallery.current()); }
      if (e.pointerId != null && heroWrap.setPointerCapture) { try { heroWrap.setPointerCapture(e.pointerId); } catch (err) {} }
    }
    function move(e) {
      if (!isHeroCarousel() || st.x === null) { return; }
      if (st.pointerId != null && e.pointerId != null && e.pointerId !== st.pointerId) { return; }
      var dx = e.clientX - st.x, dy = e.clientY - st.y;
      var ax = Math.abs(dx), ay = Math.abs(dy);
      var width = heroWrap.clientWidth, drag = dx;

      if (!st.lock) {
        if (Math.max(ax, ay) < 7) { return; }
        if (ax >= 8 && ax > ay * 1.05) {
          st.lock = 'x';
          st.dragged = true;
          st.suppressClick = true;
          heroWrap.classList.add('is-dragging');
          if (e.cancelable) { e.preventDefault(); }
        } else if (ay >= 8 && ay > ax * 1.05) {
          st.lock = 'y';
          st.suppressClick = true;
          if (heroWrap.releasePointerCapture && e.pointerId != null) { try { heroWrap.releasePointerCapture(e.pointerId); } catch (err) {} }
          return;
        } else {
          return;
        }
      }

      if (st.lock !== 'x' || !width) { return; }
      if (e.cancelable) { e.preventDefault(); }
      var i = gallery.current();
      if ((i === 0 && drag > 0) || (i === count - 1 && drag < 0)) { drag *= 0.32; }
      drag = Math.max(-width, Math.min(width, drag));
      st.delta = drag;
      heroWrap.style.setProperty('--hero-drag', drag + 'px');
    }
    function end(e) {
      if (st.x === null) { return; }
      var width = heroWrap.clientWidth;
      var threshold = width ? Math.min(72, Math.max(28, width * 0.1)) : 36;
      var duration = Math.max(1, Date.now() - st.startedAt);
      var flick = Math.abs(st.delta) > 18 && Math.abs(st.delta) / duration > 0.25;
      var i = gallery.current(), nextIndex = i;
      if (st.lock === 'x' && st.dragged && (Math.abs(st.delta) >= threshold || flick)) { nextIndex = st.delta < 0 ? Math.min(count - 1, i + 1) : Math.max(0, i - 1); }
      var shouldSuppress = st.dragged || st.suppressClick;
      var drag = st.delta;
      st.x = null; st.y = null; st.delta = 0; st.lock = null; st.pointerId = null; st.startedAt = 0;
      if (heroWrap.releasePointerCapture && e && e.pointerId != null) { try { heroWrap.releasePointerCapture(e.pointerId); } catch (err) {} }
      if (nextIndex !== i && width) {
        gallery.select(nextIndex);
        heroWrap.style.setProperty('--hero-drag', (((nextIndex - i) * width) + drag) + 'px');
        requestAnimationFrame(function () {
          heroWrap.classList.remove('is-dragging');
          heroWrap.style.setProperty('--hero-drag', '0px');
        });
      } else {
        heroWrap.classList.remove('is-dragging');
        heroWrap.style.setProperty('--hero-drag', '0px');
        if (nextIndex !== i) { gallery.select(nextIndex); }
      }
      if (shouldSuppress) { holdClickSuppression(); }
      else { st.dragged = false; st.suppressClick = false; }
    }
    if (heroWrap && window.PointerEvent) {
      heroWrap.addEventListener('pointerdown', down, { passive: true });
      heroWrap.addEventListener('pointermove', move, { passive: false });
      heroWrap.addEventListener('pointerup', end, { passive: true });
      heroWrap.addEventListener('pointercancel', end, { passive: true });
    }
    if (heroWrap) {
      heroWrap.addEventListener('touchstart', function (e) {
        touchLock = null;
        if (!isHeroCarousel() || count < 2 || !e.touches || e.touches.length !== 1) { touchStart = null; return; }
        touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }, { passive: true, capture: true });
      heroWrap.addEventListener('touchmove', function (e) {
        if (!touchStart || !isHeroCarousel() || !e.touches || e.touches.length !== 1) { return; }
        var dx = e.touches[0].clientX - touchStart.x;
        var dy = e.touches[0].clientY - touchStart.y;
        var ax = Math.abs(dx), ay = Math.abs(dy);
        if (!touchLock) {
          if (Math.max(ax, ay) < 7) { return; }
          if (ax >= 8 && ax > ay * 1.05) { touchLock = 'x'; }
          else if (ay >= 8 && ay > ax * 1.05) { touchLock = 'y'; }
          else { return; }
        }
        if (touchLock === 'x' && e.cancelable) { e.preventDefault(); }
      }, { passive: false, capture: true });
      heroWrap.addEventListener('touchend', function () { touchStart = null; touchLock = null; }, { passive: true, capture: true });
      heroWrap.addEventListener('touchcancel', function () { touchStart = null; touchLock = null; }, { passive: true, capture: true });
    }
  }

  /* === THUMB-RAIL CHEVRON (overflow scroll) === */
  function initRail(root, gallery) {
    var rail = root.querySelector('[data-pdp-rail]');
    var track = root.querySelector('[data-pdp-thumb-track]');
    var arrow = root.querySelector('[data-pdp-rail-next]');
    if (!rail || !track || !arrow) { return; }

    function horizontal() {
      return getComputedStyle(track).gridAutoFlow.indexOf('column') !== -1 || track.scrollWidth > track.clientWidth + 2;
    }
    function sync() {
      var h = horizontal();
      var max = h ? (track.scrollWidth - track.clientWidth) : (track.scrollHeight - track.clientHeight);
      var pos = h ? track.scrollLeft : track.scrollTop;
      var overflow = max > 4;
      rail.classList.toggle('has-overflow', overflow);
      var more = pos < max - 4;
      arrow.hidden = !(overflow && more);
      arrow.setAttribute('aria-label', h ? 'Scroll thumbnails right' : 'Scroll thumbnails down');
    }
    function step() {
      var first = track.querySelector('[data-pdp-thumb]');
      var h = horizontal();
      var s = first ? (h ? first.offsetWidth : first.offsetHeight) + 10 : 84;
      track.scrollBy(h ? { left: s, behavior: rm ? 'auto' : 'smooth' } : { top: s, behavior: rm ? 'auto' : 'smooth' });
      setTimeout(sync, 260);
    }
    arrow.addEventListener('click', step);
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    sync();
  }

  /* === NATIVE PHOTO VIEWER === */
  function initZoom(root, gallery) {
    var modal = root.querySelector('[data-pdp-modal]');
    var hero = root.querySelector('[data-pdp-hero-wrap]');
    var hasImages = gallery.images.some(function (im) { return !!im.src; });
    if (!modal || !hasImages) { if (hero) { hero.style.cursor = 'default'; } return; }

    var pageRoot = root.querySelector('[data-pdp-page]');
    var imgEl = modal.querySelector('[data-pdp-modal-image]');
    var counter = modal.querySelector('[data-pdp-modal-counter]');
    var prev = modal.querySelector('[data-pdp-modal-prev]');
    var next = modal.querySelector('[data-pdp-modal-next]');
    var closeBtn = modal.querySelector('[data-pdp-modal-close]');
    var frame = modal.querySelector('.modal__frame');
    var track = modal.querySelector('[data-pdp-modal-track]');
    var slotPrev = modal.querySelector('[data-pdp-modal-slot="prev"]');
    var slotCurrent = imgEl;
    var slotNext = modal.querySelector('[data-pdp-modal-slot="next"]');
    var images = gallery.images;
    var mi = 0, pendingIndex = 0, lastFocus = null, requestId = 0, closeTimer = null;
    var modalCache = {};
    var drag = { x: null, y: null, dx: 0, dy: 0, lock: null, pointerId: null, startedAt: 0 };
    var zoom = {
      scale: 1, panX: 0, panY: 0, mode: null, pointers: {},
      startX: 0, startY: 0, startPanX: 0, startPanY: 0,
      startDistance: 0, startScale: 1, lastTap: 0, lastTapX: 0, lastTapY: 0
    };
    var single = images.length <= 1;

    function normalize(i) { return (i % images.length + images.length) % images.length; }
    function isOpen() { return modal.classList.contains('is-open') || modal.open; }
    function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
    function currentGalleryIndex(target) {
      var clicked = target && target.closest ? target.closest('[data-pdp-slide]') : null;
      var active = clicked || root.querySelector('[data-pdp-slide].is-active');
      var i = active ? Number(active.getAttribute('data-index')) : gallery.current();
      if (isNaN(i) || !images[normalize(i)]) { i = gallery.current(); }
      return normalize(i);
    }
    function resetModalDrag() {
      drag.x = null; drag.y = null; drag.dx = 0; drag.dy = 0; drag.lock = null; drag.pointerId = null; drag.startedAt = 0;
      modal.classList.remove('is-dragging');
      modal.style.setProperty('--modal-drag-x', '0px');
      modal.style.setProperty('--modal-drag-y', '0px');
      modal.style.setProperty('--modal-slide-x', '0px');
      modal.style.setProperty('--modal-dismiss-scale', '1');
      modal.style.setProperty('--modal-backdrop-alpha', '1');
    }
    function pointerList() {
      return Object.keys(zoom.pointers).map(function (key) { return zoom.pointers[key]; });
    }
    function setPointer(e) {
      if (e.pointerId != null) { zoom.pointers[e.pointerId] = { x: e.clientX, y: e.clientY }; }
    }
    function deletePointer(e) {
      if (e.pointerId != null) { delete zoom.pointers[e.pointerId]; }
    }
    function distance(a, b) {
      var dx = a.x - b.x, dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    function maxPan() {
      var w = frame ? frame.clientWidth : 0;
      var h = frame ? frame.clientHeight : 0;
      return { x: Math.max(0, (w * (zoom.scale - 1)) / 2), y: Math.max(0, (h * (zoom.scale - 1)) / 2) };
    }
    function applyZoom() {
      if (zoom.scale <= 1.02) {
        zoom.scale = 1; zoom.panX = 0; zoom.panY = 0;
      } else {
        var max = maxPan();
        zoom.panX = clamp(zoom.panX, -max.x, max.x);
        zoom.panY = clamp(zoom.panY, -max.y, max.y);
      }
      modal.style.setProperty('--modal-zoom-scale', String(zoom.scale));
      modal.style.setProperty('--modal-pan-x', zoom.panX + 'px');
      modal.style.setProperty('--modal-pan-y', zoom.panY + 'px');
      modal.classList.toggle('is-zoomed', zoom.scale > 1);
    }
    function resetZoom() {
      zoom.scale = 1; zoom.panX = 0; zoom.panY = 0; zoom.mode = null; zoom.pointers = {};
      modal.classList.remove('is-zooming', 'is-zoomed');
      applyZoom();
    }
    function startPinch() {
      var pts = pointerList();
      if (pts.length < 2) { return; }
      zoom.mode = 'pinch';
      zoom.startDistance = Math.max(1, distance(pts[0], pts[1]));
      zoom.startScale = zoom.scale;
      modal.classList.add('is-zooming');
      resetModalDrag();
    }
    function updatePinch() {
      var pts = pointerList();
      if (pts.length < 2 || !zoom.startDistance) { return; }
      zoom.scale = clamp(zoom.startScale * (distance(pts[0], pts[1]) / zoom.startDistance), 1, 3);
      applyZoom();
    }
    function toggleZoomAt(x, y) {
      if (!frame) { return; }
      if (zoom.scale > 1.05) { resetZoom(); return; }
      var rect = frame.getBoundingClientRect();
      zoom.scale = 2.35;
      zoom.panX = (rect.width / 2 - (x - rect.left)) * (zoom.scale - 1);
      zoom.panY = (rect.height / 2 - (y - rect.top)) * (zoom.scale - 1);
      applyZoom();
    }
    function maybeDoubleTap(e) {
      var now = Date.now();
      var dx = e.clientX - zoom.lastTapX;
      var dy = e.clientY - zoom.lastTapY;
      if (now - zoom.lastTap < 300 && Math.sqrt(dx * dx + dy * dy) < 36) {
        zoom.lastTap = 0;
        toggleZoomAt(e.clientX, e.clientY);
        return true;
      }
      zoom.lastTap = now; zoom.lastTapX = e.clientX; zoom.lastTapY = e.clientY;
      return false;
    }
    function preloadModalImage(i) {
      var im = images[normalize(i)];
      if (!im || !im.src) { return Promise.resolve(null); }
      if (modalCache[im.src]) { return modalCache[im.src]; }
      modalCache[im.src] = new Promise(function (resolve) {
        var loader = new Image();
        loader.decoding = 'async';
        loader.onload = function () {
          if (loader.decode) {
            loader.decode().catch(noop).then(function () { resolve(loader); });
          } else {
            resolve(loader);
          }
        };
        loader.onerror = function () { resolve(loader); };
        loader.src = im.src;
      });
      return modalCache[im.src];
    }
    function warmModalAround(i) {
      preloadModalImage(i);
      if (!single) {
        preloadModalImage(normalize(i + 1));
        preloadModalImage(normalize(i - 1));
      }
    }
    function fillSlot(el, i, active) {
      var im = images[normalize(i)];
      if (!el || !im) { return; }
      el.decoding = 'async';
      el.loading = 'eager';
      if (el.getAttribute('src') !== im.src) { el.src = im.src; }
      el.alt = active ? (im.alt || '') : '';
      el.setAttribute('aria-hidden', active ? 'false' : 'true');
    }
    function renderTrack(i) {
      if (track) {
        fillSlot(slotPrev, i - 1, false);
        fillSlot(slotCurrent, i, true);
        fillSlot(slotNext, i + 1, false);
      } else if (imgEl) {
        var im = images[normalize(i)];
        imgEl.src = im.src;
        imgEl.alt = im.alt || '';
      }
    }
    function commitModal(i) {
      requestId++;
      pendingIndex = normalize(i);
      mi = pendingIndex;
      resetZoom();
      renderTrack(mi);
      modal.classList.remove('is-loading');
      syncChrome();
      warmModalAround(mi);
    }

    if (single) { if (prev) prev.hidden = true; if (next) next.hidden = true; }

    function syncChrome() {
      if (!images[mi]) { return; }
      if (counter) { counter.textContent = (mi + 1) + ' of ' + images.length; }
    }
    function selectModal(i) {
      pendingIndex = normalize(i);
      var token = ++requestId;
      var im = images[pendingIndex];
      modal.classList.add('is-loading');
      resetZoom();
      renderTrack(pendingIndex);
      preloadModalImage(pendingIndex).then(function () {
        if (token !== requestId || !isOpen()) { return; }
        mi = pendingIndex;
        renderTrack(mi);
        modal.classList.remove('is-loading');
        syncChrome();
        warmModalAround(mi);
      });
    }
    function stepModal(d) { selectModal(pendingIndex + d); }
    function settleSlide(dx) {
      var dir = dx < 0 ? 1 : -1;
      var offscreen = (dir > 0 ? -1 : 1) * Math.max(frame ? frame.clientWidth : 0, window.innerWidth || 0, 320);
      drag.x = null; drag.y = null; drag.lock = null; drag.pointerId = null;
      modal.classList.remove('is-dragging');
      modal.style.setProperty('--modal-slide-x', offscreen + 'px');
      window.setTimeout(function () {
        modal.classList.add('is-dragging');
        modal.style.setProperty('--modal-slide-x', '0px');
        commitModal(mi + dir);
        requestAnimationFrame(function () { modal.classList.remove('is-dragging'); });
      }, rm ? 0 : 220);
    }
    function finishClose() {
      window.clearTimeout(closeTimer);
      requestId++;
      resetModalDrag();
      resetZoom();
      modal.classList.remove('is-open', 'is-loading', 'is-closing'); modal.setAttribute('aria-hidden', 'true'); modal.setAttribute('inert', '');
      if (imgEl) { imgEl.removeAttribute('src'); imgEl.alt = ''; }
      if (modal.open && modal.close) { try { modal.close(); } catch (err) {} }
      if (pageRoot) { pageRoot.removeAttribute('inert'); pageRoot.removeAttribute('aria-hidden'); }
      document.documentElement.classList.remove('pdp-is-locked'); document.body.classList.remove('pdp-is-locked');
      if (lastFocus && lastFocus.focus) { lastFocus.focus({ preventScroll: true }); }
      lastFocus = null;
    }
    function close() {
      if (!isOpen() || modal.classList.contains('is-closing')) { return; }
      requestId++;
      if (gallery.select) { gallery.select(mi, { behavior: 'auto' }); }
      if (!rm) {
        modal.style.setProperty('--modal-backdrop-alpha', '0');
        modal.classList.add('is-closing');
        closeTimer = window.setTimeout(finishClose, 160);
        return;
      }
      finishClose();
    }
    function releaseFramePointer(e) {
      if (frame && frame.releasePointerCapture && e.pointerId != null) {
        try { frame.releasePointerCapture(e.pointerId); } catch (err) {}
      }
    }
    function startPan(e) {
      zoom.mode = 'pan';
      zoom.startX = e.clientX; zoom.startY = e.clientY;
      zoom.startPanX = zoom.panX; zoom.startPanY = zoom.panY;
      modal.classList.add('is-zooming');
    }
    function stopZoomGesture(e) {
      releaseFramePointer(e);
      deletePointer(e);
      if (Object.keys(zoom.pointers).length < 2) { zoom.mode = null; modal.classList.remove('is-zooming'); }
      applyZoom();
    }

    function open(e) {
      mi = currentGalleryIndex(e && e.target); lastFocus = document.activeElement;
      pendingIndex = mi;
      window.clearTimeout(closeTimer);
      resetModalDrag();
      resetZoom();
      if (hero && hero.focus) { hero.focus({ preventScroll: true }); }
      modal.removeAttribute('inert'); modal.setAttribute('aria-hidden', 'false');
      if (pageRoot) { pageRoot.setAttribute('inert', ''); pageRoot.setAttribute('aria-hidden', 'true'); }
      document.documentElement.classList.add('pdp-is-locked'); document.body.classList.add('pdp-is-locked');
      if (modal.showModal && !modal.open) {
        try { modal.showModal(); } catch (err) {}
      }
      modal.classList.add('is-open');
      commitModal(mi);
      requestAnimationFrame(function () { if (closeBtn) closeBtn.focus({ preventScroll: true }); });
    }

    if (hero) {
      hero.addEventListener('click', function (e) {
        var st = root.__pdpHeroDrag;
        if (st && (st.dragged || st.suppressClick)) {
          if (st.timer) { clearTimeout(st.timer); st.timer = null; }
          st.dragged = false; st.suppressClick = false; e.preventDefault(); e.stopPropagation(); return;
        }
        open(e);
      });
      hero.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    }
    if (closeBtn) closeBtn.addEventListener('click', function () { close(); });
    if (prev) prev.addEventListener('click', function () { stepModal(-1); });
    if (next) next.addEventListener('click', function () { stepModal(1); });
    modal.addEventListener('click', function (e) {
      if (e.target.closest('.modal__frame, .modal__nav, .modal__close, .modal__label')) { return; }
      close();
    });
    modal.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) { return; }
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowRight') { stepModal(1); }
      else if (e.key === 'ArrowLeft') { stepModal(-1); }
      else if (e.key === 'Home') { selectModal(0); }
      else if (e.key === 'End') { selectModal(images.length - 1); }
      else if (e.key === 'Tab') {
        var foc = Array.prototype.filter.call(
          modal.querySelectorAll('button, [tabindex="0"]'),
          function (el) { return !el.hidden && !el.disabled && el.offsetParent !== null; });
        if (!foc.length) { e.preventDefault(); return; }
        var first = foc[0], last = foc[foc.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    if (frame) {
      frame.addEventListener('pointerdown', function (e) {
        if (!isOpen() || (e.pointerType === 'mouse' && e.button !== 0)) { return; }
        setPointer(e);
        if (frame.setPointerCapture && e.pointerId != null) { try { frame.setPointerCapture(e.pointerId); } catch (err) {} }
        if (Object.keys(zoom.pointers).length >= 2) { startPinch(); if (e.cancelable) { e.preventDefault(); } return; }
        if (zoom.scale > 1) { startPan(e); if (e.cancelable) { e.preventDefault(); } return; }
        drag.x = e.clientX; drag.y = e.clientY; drag.dx = 0; drag.dy = 0; drag.lock = null; drag.pointerId = e.pointerId; drag.startedAt = Date.now();
        renderTrack(mi);
        warmModalAround(mi);
      }, { passive: false });
      frame.addEventListener('pointermove', function (e) {
        if (zoom.pointers[e.pointerId]) { setPointer(e); }
        if (zoom.mode === 'pinch') {
          if (e.cancelable) { e.preventDefault(); }
          updatePinch();
          return;
        }
        if (zoom.mode === 'pan') {
          if (e.cancelable) { e.preventDefault(); }
          zoom.panX = zoom.startPanX + e.clientX - zoom.startX;
          zoom.panY = zoom.startPanY + e.clientY - zoom.startY;
          applyZoom();
          return;
        }
        if (drag.x === null || (drag.pointerId != null && e.pointerId != null && e.pointerId !== drag.pointerId)) { return; }
        var dx = e.clientX - drag.x;
        var dy = e.clientY - drag.y;
        var ax = Math.abs(dx);
        var ay = Math.abs(dy);
        if (!drag.lock) {
          if (Math.max(ax, ay) < 7) { return; }
          if (!single && ax >= 8 && ax > ay) { drag.lock = 'x'; modal.classList.add('is-dragging'); }
          else if (dy > 0 && ay > ax * 1.2 && ay > 10) { drag.lock = 'y'; modal.classList.add('is-dragging'); }
          else if (ay > ax * 1.35 && ay > 10) { drag.lock = 'ignore'; }
          else { return; }
        }
        if (drag.lock === 'y') {
          if (e.cancelable) { e.preventDefault(); }
          var pull = Math.max(0, dy);
          drag.dy = pull;
          modal.style.setProperty('--modal-drag-y', pull + 'px');
          modal.style.setProperty('--modal-backdrop-alpha', String(clamp(1 - pull / 360, .52, 1)));
          return;
        }
        if (drag.lock !== 'x') { return; }
        if (e.cancelable) { e.preventDefault(); }
        drag.dx = dx; drag.dy = dy;
        modal.style.setProperty('--modal-slide-x', dx + 'px');
      }, { passive: false });
      frame.addEventListener('pointerup', function (e) {
        if (zoom.mode === 'pinch' || zoom.mode === 'pan') { stopZoomGesture(e); return; }
        deletePointer(e);
        if (drag.x === null) { return; }
        var dx = drag.dx;
        var dy = drag.dy;
        var lock = drag.lock;
        var elapsed = Math.max(1, Date.now() - drag.startedAt);
        var yVelocity = dy / elapsed;
        releaseFramePointer(e);
        if ((lock === null || lock === 'ignore') && Math.abs(dx) < 8 && Math.abs(dy) < 8 && maybeDoubleTap(e)) { resetModalDrag(); return; }
        if (lock === 'y' && (dy > 94 || yVelocity > .55)) { close(); return; }
        if (lock === 'x' && Math.abs(dx) > 54) { settleSlide(dx); return; }
        resetModalDrag();
      }, { passive: true });
      frame.addEventListener('pointercancel', function (e) {
        deletePointer(e);
        zoom.mode = null;
        modal.classList.remove('is-zooming');
        resetModalDrag();
      }, { passive: true });
      frame.addEventListener('dblclick', function (e) { e.preventDefault(); toggleZoomAt(e.clientX, e.clientY); });
    }
  }

  /* === DELIVERY COUNTDOWN: LA time, 3:00 PM cutoff === */
  function initDeliveryCountdown(root) {
    var cards = root.querySelectorAll('[data-pdp-delivery-card]');
    if (!cards.length) { return; }

    function losAngelesNow() {
      var parts = {};
      var formatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date());
      formatted.forEach(function (part) {
        if (part.type !== 'literal') { parts[part.type] = part.value; }
      });
      return new Date(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
    }

    function pad(value) {
      return ('0' + Math.max(0, value)).slice(-2);
    }

    function updateCard(card) {
      var title = card.querySelector('[data-pdp-delivery-title]');
      var label = card.querySelector('[data-pdp-delivery-countdown-label]');
      var cutoffLabel = card.querySelector('[data-pdp-delivery-copy]');
      var hoursEl = card.querySelector('[data-pdp-delivery-hours]');
      var minutesEl = card.querySelector('[data-pdp-delivery-minutes]');
      var secondsEl = card.querySelector('[data-pdp-delivery-seconds]');
      if (!title || !label || !cutoffLabel || !hoursEl || !minutesEl || !secondsEl) { return; }

      var now = losAngelesNow();
      var cutoffHour = Number(card.getAttribute('data-pdp-delivery-cutoff-hour')) || 15;
      var cutoffMinute = Number(card.getAttribute('data-pdp-delivery-cutoff-minute')) || 0;
      var cutoff = new Date(now);
      cutoff.setHours(cutoffHour, cutoffMinute, 0, 0);
      var isTomorrow = now > cutoff;
      if (isTomorrow) { cutoff.setDate(cutoff.getDate() + 1); }

      var remainingSeconds = Math.max(0, Math.floor((cutoff.getTime() - now.getTime()) / 1000));
      var hours = Math.floor(remainingSeconds / 3600);
      var minutes = Math.floor((remainingSeconds % 3600) / 60);
      var seconds = remainingSeconds % 60;

      title.textContent = isTomorrow ? 'Get it tomorrow' : 'Get it today';
      label.textContent = 'Order within';
      cutoffLabel.textContent = card.getAttribute('data-pdp-delivery-cutoff-label') || '3:00 PM';
      hoursEl.textContent = String(hours);
      minutesEl.textContent = pad(minutes);
      secondsEl.textContent = pad(seconds);
    }

    Array.prototype.forEach.call(cards, function (card) {
      updateCard(card);
      window.setInterval(function () { updateCard(card); }, 1000);
    });
  }

  /* === DESCRIPTION expand/collapse + detail-cards collapse === */
  function initDescription(root) {
    var shell = root.querySelector('[data-pdp-desc-shell]');
    var desc = root.querySelector('[data-pdp-desc]');
    var toggle = root.querySelector('[data-pdp-desc-toggle]');
    var info = root.querySelector('[data-pdp-info]');
    if (!shell || !desc || !toggle) { return; }
    var label = toggle.querySelector('[data-pdp-desc-toggle-text]');
    var transitionTimer = null;

    function overflowing() {
      var cs = getComputedStyle(desc);
      var lh = parseFloat(cs.lineHeight) || 22;
      return desc.scrollHeight > lh * 4 + 4;
    }
    function syncOverflow() {
      var wasExpanded = shell.classList.contains('is-expanded');
      var of = overflowing();
      shell.classList.toggle('is-overflowing', of);
      toggle.hidden = !of;
      if (!of && wasExpanded) {
        setExpanded(false);
      } else if (of && wasExpanded) {
        setExpandedHeightVar();
      }
    }
    function resetScroll() {
      desc.scrollTop = 0;
      requestAnimationFrame(function () { desc.scrollTop = 0; });
    }
    function expandedHeight() {
      return Math.ceil(desc.scrollHeight);
    }
    function setExpandedHeightVar() {
      shell.style.setProperty('--description-expanded-height', expandedHeight() + 'px');
    }
    function markAnimating() {
      window.clearTimeout(transitionTimer);
      shell.classList.add('is-animating');
      transitionTimer = window.setTimeout(function () {
        shell.classList.remove('is-animating');
      }, 360);
    }
    function setExpanded(exp) {
      setExpandedHeightVar();
      markAnimating();
      if (!exp) { resetScroll(); }
      shell.classList.toggle('is-expanded', exp);
      if (info) { info.classList.toggle('is-description-expanded', exp); }
      toggle.setAttribute('aria-expanded', exp ? 'true' : 'false');
      if (label) { label.textContent = exp ? 'Show less' : 'Show more'; }
      if (!exp) { syncOverflow(); resetScroll(); }
    }
    desc.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'max-height') {
        window.clearTimeout(transitionTimer);
        shell.classList.remove('is-animating');
      }
    });
    toggle.addEventListener('click', function () { setExpanded(toggle.getAttribute('aria-expanded') !== 'true'); });
    window.addEventListener('resize', syncOverflow);
    window.addEventListener('load', syncOverflow);
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(syncOverflow); }
    syncOverflow();
  }

  /* === QUANTITY STEPPER === */
  function initQty(root) {
    var input = root.querySelector('[data-pdp-qty-input]');
    if (!input) { return; }
    function val() { var n = parseInt(input.value, 10); return (isNaN(n) || n < 1) ? 1 : n; }
    function set(n) { input.value = Math.max(1, Math.floor(n)); }
    var minus = root.querySelector('[data-pdp-qty-minus]');
    var plus = root.querySelector('[data-pdp-qty-plus]');
    if (minus) minus.addEventListener('click', function () { set(val() - 1); });
    if (plus) plus.addEventListener('click', function () { set(val() + 1); });
    input.addEventListener('change', function () { set(val()); });
    input.addEventListener('blur', function () { set(val()); });
  }

  /* === VARIANT SELECTION === */
  function initVariants(root, gallery) {
    var dataEl = root.querySelector('[data-pdp-variant-json]');
    if (!dataEl) { return; }
    var data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var variants = (data && data.variants) || [];
    var inputs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-variant-input]'));
    var idField = root.querySelector('[data-pdp-id-field]') || root.querySelector('[data-pdp-variant-id]');
    if (variants.length <= 1 || inputs.length === 0) { return; }
    if (idField && !idField.getAttribute('name')) { idField.setAttribute('name', idField.getAttribute('data-pdp-id-name') || 'id'); }

    var addBtn = root.querySelector('[data-pdp-add]');
    var addLabel = root.querySelector('[data-pdp-add-label]');
    var priceNow = root.querySelector('[data-pdp-price-now]');
    var priceWas = root.querySelector('[data-pdp-price-was]');
    var moneyFmt = (data && (data.currency_code_enabled ? (data.money_format_with_currency || data.money_format) : data.money_format)) || '${{amount}}';

    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return String(moneyFmt)
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)));
    }
    function selectedValues() {
      var by = {};
      inputs.forEach(function (i) { if (i.checked) { by[i.getAttribute('data-option-index')] = i.value; } });
      return by;
    }
    function match() {
      var sel = selectedValues();
      return variants.find(function (v) { return Object.keys(sel).every(function (k) { return String(v.options[k]) === String(sel[k]); }); });
    }
    function apply(v) {
      if (!v) {
        if (!root.__pdpCartSubmitting) {
          if (addBtn) addBtn.disabled = true;
          if (addLabel) addLabel.textContent = 'Unavailable';
        }
        return;
      }
      if (idField) idField.value = String(v.id);
      if (priceNow) priceNow.innerHTML = formatMoney(v.price);
      if (priceWas) {
        if (v.compare_at_price && v.compare_at_price > v.price) { priceWas.hidden = false; priceWas.innerHTML = formatMoney(v.compare_at_price); }
        else { priceWas.hidden = true; }
      }
      if (!root.__pdpCartSubmitting) {
        if (addBtn) addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? 'Add to cart' : 'Sold out';
      }
      if (v.featured_media && v.featured_media.id) { gallery.selectByMediaId(v.featured_media.id); }
    }
    inputs.forEach(function (i) { i.addEventListener('change', function () { apply(match()); }); });
  }

  /* === ADD TO CART (works w/o JS; fetch enhancement) === */
  function initCart(root) {
    var wrapper = root.querySelector('[data-pdp-form]');
    if (!wrapper) { return; }
    var form = wrapper.closest('form');
    if (!form) { return; }
    var addBtn = root.querySelector('[data-pdp-add]');
    var err = root.querySelector('[data-pdp-error]');
    var addUrl = root.getAttribute('data-cart-add-url') || '/cart/add';
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';

    function setLoading(active) {
      root.__pdpCartSubmitting = active;
      if (!addBtn) { return; }
      if (active) {
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addBtn.setAttribute('aria-busy', 'true');
      } else {
        addBtn.disabled = false;
        addBtn.classList.remove('is-loading');
        addBtn.removeAttribute('aria-busy');
      }
    }

    function resetStaleLoading() {
      if (root.__pdpCartSubmitting || (addBtn && addBtn.classList.contains('is-loading'))) {
        setLoading(false);
      }
    }

    window.addEventListener('pagehide', resetStaleLoading);
    window.addEventListener('pageshow', resetStaleLoading);

    form.addEventListener('submit', function (e) {
      if (!window.fetch || !window.FormData) { return; }
      e.preventDefault();
      if (root.__pdpCartSubmitting) { return; }
      if (err) { err.hidden = true; err.textContent = ''; }
      setLoading(true);
      fetch(addUrl + '.js', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) { throw new Error((res.body && res.body.description) || 'Could not add to cart'); }
          window.location.href = cartUrl;
        })
        .catch(function (ex) {
          if (err) { err.hidden = false; err.textContent = ex.message || 'Could not add to cart'; }
          setLoading(false);
        });
    });
  }

  /* === UPSELL CAROUSEL === */
  function initUpsell(root) {
    var track = root.querySelector('[data-pdp-upsell-track]');
    var prev = root.querySelector('[data-pdp-upsell-prev]');
    var next = root.querySelector('[data-pdp-upsell-next]');
    if (!track || !prev || !next) { return; }

    Array.prototype.forEach.call(track.querySelectorAll('.upsell__media'), function (media) {
      var img = media.querySelector('img');
      if (!img) { return; }
      img.decoding = 'async';
      function done() { media.classList.remove('is-loading'); media.classList.add('is-loaded'); }
      if (img.complete && img.naturalWidth > 0) { done(); }
      else {
        media.classList.add('is-loading');
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });

    function step() {
      var card = track.querySelector('.upsell__card');
      if (!card) { return track.clientWidth; }
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap);
      if (!Number.isFinite(gap)) { gap = parseFloat(styles.gap) || 0; }
      return card.getBoundingClientRect().width + gap;
    }
    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }
    function snapTarget(direction) {
      var max = maxScroll();
      var x = track.scrollLeft;
      var s = step();
      if (max <= 1 || s <= 0) { return direction > 0 ? max : 0; }
      if (direction < 0) {
        if (x >= max - 1) { return Math.max(0, Math.floor((max - 1) / s) * s); }
        return Math.max(0, Math.floor((x - 1) / s) * s);
      }
      return Math.min(max, Math.ceil((x + 1) / s) * s);
    }
    function update() {
      var max = maxScroll();
      var x = track.scrollLeft;
      prev.disabled = x <= 1;
      next.disabled = x >= max - 1;
      if (max <= 1) {
        track.style.setProperty('--fade-left', '0px');
        track.style.setProperty('--fade-right', '0px');
        return;
      }
      var uFade = parseFloat(getComputedStyle(track).getPropertyValue('--u-fade')) || 0;
      track.style.setProperty('--fade-right', Math.max(0, Math.min(uFade, max - x)) + 'px');
      track.style.setProperty('--fade-left', Math.max(0, Math.min(uFade, x)) + 'px');
    }
    prev.addEventListener('click', function () { track.scrollTo({ left: snapTarget(-1), behavior: rm ? 'auto' : 'smooth' }); });
    next.addEventListener('click', function () { track.scrollTo({ left: snapTarget(1), behavior: rm ? 'auto' : 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
})();
